import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Menu, Icon } from 'antd';
import Cookies from 'js-cookie';

import { CONFIG_STATE_TYPE } from 'globals/constants';
import { getSyncStoreCurrConfig } from 'globals/selectors';
import withSyncStoreRouterSpecialisation from 'globals/withSyncStoreRouterSpecialisation';

import {
  SideMenu,
  SideMenuToggle,
  EllipsisLink,
  ResetIcon,
  ResetText,
} from './styles';

const locationToMenuItem = location => {
  const secondSlashIndex = location.pathname.indexOf('/', 1);
  const thirdSlashIndex = location.pathname.indexOf('/', secondSlashIndex + 1);
  if (thirdSlashIndex === -1) {
    return 'dashboard';
  }
  const subPathname = location.pathname.slice(thirdSlashIndex + 1);
  const subSlashIndex = subPathname.indexOf('/');
  return subSlashIndex === -1
    ? subPathname.toLowerCase()
    : subPathname.slice(0, subSlashIndex).toLowerCase();
};

function Sider({ location, specialisation }) {
  const currConfig = useSelector(getSyncStoreCurrConfig);

  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  let currState;
  if (currConfig === undefined || currConfig === null || !currConfig.state) {
    currState = -1;
  } else {
    try {
      currState = JSON.parse(Cookies.get('selectedAcademicSession')).isCurr
        ? currConfig.state.type
        : CONFIG_STATE_TYPE.COMPLETE;
    } catch (error) {
      currState = currConfig.state.type;
    }
  }

  if (!specialisation) {
    return null;
  }
  if (/^\/\w+\/admin/.test(location.pathname)) {
    return (
      <SideMenu collapsible collapsed={isCollapsed} trigger={null}>
        <SideMenuToggle onClick={toggleCollapsed}>
          <Icon type={isCollapsed ? 'menu-unfold' : 'menu-fold'} />
        </SideMenuToggle>
        <Menu
          theme="light"
          selectedKeys={[locationToMenuItem(location)]}
          mode="inline"
        >
          <Menu.Item key="dashboard">
            <EllipsisLink to={`/${specialisation}/admin`}>
              <Icon type="dashboard" />
              <span>Dashboard</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.SubMenu
            key="userManagement"
            title={
              <span>
                <Icon type="team" />
                <span>User Management</span>
              </span>
            }
          >
            {currState >= CONFIG_STATE_TYPE.NEW ? (
              <Menu.Item key="lecturer">
                <EllipsisLink to={`/${specialisation}/admin/lecturer`}>
                  <Icon type="user" />
                  <span>Lecturer</span>
                </EllipsisLink>
              </Menu.Item>
            ) : null}
            {currState >= CONFIG_STATE_TYPE.NEW ? (
              <Menu.Item key="student">
                <EllipsisLink to={`/${specialisation}/admin/student`}>
                  <Icon type="user" />
                  <span>Student</span>
                </EllipsisLink>
              </Menu.Item>
            ) : null}
            <Menu.Item key="coordinator">
              <EllipsisLink
                to={`/${specialisation}/admin/coordinator/resetpassword`}
              >
                <Icon type="user" />
                <span>Coordinator</span>
              </EllipsisLink>
            </Menu.Item>
          </Menu.SubMenu>
          <Menu.Item key="setting">
            <EllipsisLink to={`/${specialisation}/admin/setting`}>
              <Icon type="setting" />
              <span>Setting</span>
            </EllipsisLink>
          </Menu.Item>
        </Menu>
      </SideMenu>
    );
  }
  if (
    /^\/\w+\/coordinator/.test(location.pathname) &&
    currState >= CONFIG_STATE_TYPE.COORD_READY
  ) {
    return (
      <SideMenu collapsible collapsed={isCollapsed} trigger={null}>
        <SideMenuToggle onClick={toggleCollapsed}>
          <Icon type={isCollapsed ? 'menu-unfold' : 'menu-fold'} />
        </SideMenuToggle>
        <Menu
          theme="light"
          selectedKeys={[locationToMenuItem(location)]}
          mode="inline"
        >
          <Menu.Item key="dashboard">
            <EllipsisLink to={`/${specialisation}/coordinator`}>
              <Icon type="dashboard" />
              <span>Dashboard</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="mainconfig">
            <EllipsisLink to={`/${specialisation}/coordinator/mainconfig`}>
              <Icon type="setting" />
              <span>Main Configurations</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="projectcategory">
            <EllipsisLink to={`/${specialisation}/coordinator/projectcategory`}>
              <Icon type="table" />
              <span>Project Categorisation</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="gradeevaluation">
            <EllipsisLink to={`/${specialisation}/coordinator/gradeevaluation`}>
              <Icon type="check-square" />
              <span>Grade Evaluation</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="biddingprocess">
            <EllipsisLink to={`/${specialisation}/coordinator/biddingprocess`}>
              <Icon type="clock-circle" />
              <span>Bidding Process</span>
            </EllipsisLink>
          </Menu.Item>
          {currState >= CONFIG_STATE_TYPE.EXAMINER_READY ? (
            <Menu.Item key="roleassignment">
              <EllipsisLink
                to={`/${specialisation}/coordinator/roleassignment`}
              >
                <Icon type="user" />
                <span>Role Assignment</span>
              </EllipsisLink>
            </Menu.Item>
          ) : null}
          {currState >= CONFIG_STATE_TYPE.LECT_READY ? (
            <Menu.Item key="viewprojects">
              <EllipsisLink to={`/${specialisation}/coordinator/viewprojects`}>
                <Icon type="file-search" />
                <span>View Projects</span>
              </EllipsisLink>
            </Menu.Item>
          ) : null}
          {currState >= CONFIG_STATE_TYPE.LECT_READY ? (
            <Menu.SubMenu
              key="summaryreport"
              title={
                <span>
                  <Icon type="file" />
                  <span>Summary & Report</span>
                </span>
              }
            >
              <Menu.Item key="projectcount">
                <EllipsisLink
                  to={`/${specialisation}/coordinator/projectcount`}
                >
                  <Icon type="plus-square" />
                  <span>Project Count</span>
                </EllipsisLink>
              </Menu.Item>
              <Menu.Item key="studentprojectsupervisor">
                <EllipsisLink
                  to={`/${specialisation}/coordinator/studentprojectsupervisor`}
                >
                  <Icon type="user" />
                  <span>Students Project</span>
                </EllipsisLink>
              </Menu.Item>
              {currConfig.state.type < CONFIG_STATE_TYPE.BID_READY ? null : (
                <Menu.Item key="biddingstatus">
                  <EllipsisLink
                    to={`/${specialisation}/coordinator/biddingstatus`}
                  >
                    <Icon type="question-circle" />
                    <span>Bidding Status</span>
                  </EllipsisLink>
                </Menu.Item>
              )}
              {currConfig.state.type <
              CONFIG_STATE_TYPE.EVALUATION_READY ? null : (
                <Menu.Item key="projectsgrades">
                  <EllipsisLink
                    to={`/${specialisation}/coordinator/projectsgrades`}
                  >
                    <Icon type="diff" />
                    <span>Projects Grades</span>
                  </EllipsisLink>
                </Menu.Item>
              )}
            </Menu.SubMenu>
          ) : null}
          <Menu.Item key="reset">
            <EllipsisLink to={`/${specialisation}/coordinator/reset`}>
              <ResetIcon type="undo" />
              <ResetText>Reset</ResetText>
            </EllipsisLink>
          </Menu.Item>
        </Menu>
      </SideMenu>
    );
  }
  if (
    /^\/\w+\/lecturer/.test(location.pathname) &&
    currState >= CONFIG_STATE_TYPE.LECT_READY
  ) {
    return (
      <SideMenu collapsible collapsed={isCollapsed} trigger={null}>
        <SideMenuToggle onClick={toggleCollapsed}>
          <Icon type={isCollapsed ? 'menu-unfold' : 'menu-fold'} />
        </SideMenuToggle>
        <Menu
          theme="light"
          selectedKeys={[locationToMenuItem(location)]}
          mode="inline"
        >
          <Menu.Item key="dashboard">
            <EllipsisLink to={`/${specialisation}/lecturer`}>
              <Icon type="dashboard" />
              <span>Dashboard</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="myprofile">
            <EllipsisLink to={`/${specialisation}/lecturer/myprofile`}>
              <Icon type="user" />
              <span>My Profile</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.SubMenu
            key="project"
            title={
              <span>
                <Icon type="file" />
                <span>Project</span>
              </span>
            }
          >
            <Menu.Item key="manageproject">
              <EllipsisLink to={`/${specialisation}/lecturer/manageproject`}>
                <Icon type="project" />
                <span>Manage Project</span>
              </EllipsisLink>
            </Menu.Item>
            {currState >= CONFIG_STATE_TYPE.TITLE_READY &&
            currConfig.isProjectVisibleToOtherLecturers ? (
              <Menu.Item key="viewotherproject">
                <EllipsisLink
                  to={`/${specialisation}/lecturer/viewotherproject`}
                >
                  <Icon type="file-search" />
                  <span>Other Projects</span>
                </EllipsisLink>
              </Menu.Item>
            ) : null}
          </Menu.SubMenu>
          {currState >= CONFIG_STATE_TYPE.BID_READY ? (
            <Menu.Item key="projectbidding">
              <EllipsisLink to={`/${specialisation}/lecturer/projectbidding`}>
                <Icon type="profile" />
                <span>Project Bidding</span>
              </EllipsisLink>
            </Menu.Item>
          ) : null}
          {currState >= CONFIG_STATE_TYPE.EVALUATION_READY ? (
            <Menu.SubMenu
              key="gradeevaluation"
              title={
                <span>
                  <Icon type="check-square" />
                  <span>Grade Evaluation</span>
                </span>
              }
            >
              <Menu.Item key="supervisorevaluation">
                <EllipsisLink
                  to={`/${specialisation}/lecturer/supervisorevaluation`}
                >
                  <Icon type="user" />
                  <span>Supervisor</span>
                </EllipsisLink>
              </Menu.Item>
              <Menu.Item key="examinerevaluation">
                <EllipsisLink
                  to={`/${specialisation}/lecturer/examinerevaluation`}
                >
                  <Icon type="user" />
                  <span>Examiner</span>
                </EllipsisLink>
              </Menu.Item>
            </Menu.SubMenu>
          ) : null}
        </Menu>
      </SideMenu>
    );
  }
  if (
    /^\/\w+\/student/.test(location.pathname) &&
    currState >= CONFIG_STATE_TYPE.TITLE_READY
  ) {
    return (
      <SideMenu collapsible collapsed={isCollapsed} trigger={null}>
        <SideMenuToggle onClick={toggleCollapsed}>
          <Icon type={isCollapsed ? 'menu-unfold' : 'menu-fold'} />
        </SideMenuToggle>
        <Menu
          theme="light"
          selectedKeys={[locationToMenuItem(location)]}
          mode="inline"
        >
          <Menu.Item key="dashboard">
            <EllipsisLink to={`/${specialisation}/student`}>
              <Icon type="dashboard" />
              <span>Dashboard</span>
            </EllipsisLink>
          </Menu.Item>
          <Menu.Item key="myprofile">
            <EllipsisLink to={`/${specialisation}/student/myprofile`}>
              <Icon type="user" />
              <span>My Profile</span>
            </EllipsisLink>
          </Menu.Item>
          {/* <Menu.Item key="mygroup">
            <EllipsisLink to={`/${specialisation}/student/mygroup`}>
              <Icon type="user" />
              <span>My Group</span>
            </EllipsisLink>
          </Menu.Item> */}
          <Menu.Item key="projectoffer">
            <EllipsisLink to={`/${specialisation}/student/projectoffer`}>
              <Icon type="project" />
              <span>Project Offer</span>
            </EllipsisLink>
          </Menu.Item>
          {currState >= CONFIG_STATE_TYPE.BID_READY ? (
            <Menu.Item key="projectbidding">
              <EllipsisLink to={`/${specialisation}/student/projectbidding`}>
                <Icon type="profile" />
                <span>Project Bidding</span>
              </EllipsisLink>
            </Menu.Item>
          ) : null}
          {currState >= CONFIG_STATE_TYPE.EVALUATION_READY ? (
            <Menu.Item key="projectevaluation">
              <EllipsisLink to={`/${specialisation}/student/projectevaluation`}>
                <Icon type="file-done" />
                <span>Evaluation</span>
              </EllipsisLink>
            </Menu.Item>
          ) : null}
        </Menu>
      </SideMenu>
    );
  }
  return null;
}

Sider.propTypes = {
  location: PropTypes.object.isRequired,
  specialisation: PropTypes.string,
};
Sider.defaultProps = {
  specialisation: null,
};

export default withSyncStoreRouterSpecialisation(Sider, ['currConfig']);
